import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const ThemeCtx = createContext(null);

const STORAGE_KEY = "md-theme-mode";

// Map hex -> HSL channels "h s% l%" for CSS variables
function hexToHsl(hex) {
    const m = hex.replace("#", "");
    const r = parseInt(m.substring(0, 2), 16) / 255;
    const g = parseInt(m.substring(2, 4), 16) / 255;
    const b = parseInt(m.substring(4, 6), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
            default: break;
        }
        h *= 60;
    }
    return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

const VAR_MAP = {
    background: "--md-bg",
    surface: "--md-surface",
    surface_elevated: "--md-surface-2",
    primary: "--md-primary",
    secondary: "--md-secondary",
    accent_success: "--md-accent",
    warning: "--md-warning",
    danger: "--md-danger",
    text_primary: "--md-text",
    text_secondary: "--md-text-2",
    border: "--md-border",
};

const RARITY_MAP = {
    counco: "--md-rarity-commune",
    rare: "--md-rarity-rare",
    holo: "--md-rarity-holo",
    reverse: "--md-rarity-commune-reverse",
    ex_v: "--md-rarity-ex-v",
};

function applyTheme(theme, mode) {
    if (!theme) return;
    const root = document.documentElement;
    const palette = mode === "dark" ? theme.dark : theme.light;
    if (palette) {
        Object.entries(palette).forEach(([k, v]) => {
            const cssVar = VAR_MAP[k];
            if (cssVar && typeof v === "string" && v.startsWith("#")) {
                root.style.setProperty(cssVar, hexToHsl(v));
            }
        });
    }
    if (theme.rarity_colors) {
        Object.entries(theme.rarity_colors).forEach(([k, v]) => {
            const cssVar = RARITY_MAP[k];
            if (cssVar && typeof v === "string" && v.startsWith("#")) {
                root.style.setProperty(cssVar, hexToHsl(v));
            }
        });
    }
}

export function ThemeProvider({ children }) {
    const [mode, setMode] = useState(() => {
        if (typeof window === "undefined") return "light";
        return localStorage.getItem(STORAGE_KEY) || "light";
    });
    const [theme, setTheme] = useState(null);

    useEffect(() => {
        const root = document.documentElement;
        if (mode === "dark") root.classList.add("dark");
        else root.classList.remove("dark");
        localStorage.setItem(STORAGE_KEY, mode);
    }, [mode]);

    useEffect(() => {
        let cancelled = false;
        api.get("/public/brand-theme")
            .then(({ data }) => {
                if (!cancelled) {
                    setTheme(data);
                    applyTheme(data, mode);
                }
            })
            .catch(() => { /* fallback to CSS defaults */ });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // load once

    useEffect(() => {
        applyTheme(theme, mode);
    }, [theme, mode]);

    return (
        <ThemeCtx.Provider value={{
            mode,
            toggle: () => setMode((m) => (m === "dark" ? "light" : "dark")),
            setMode,
            theme,
            setTheme,
            applyTheme: (t) => applyTheme(t, mode),
        }}>
            {children}
        </ThemeCtx.Provider>
    );
}

export function useTheme() {
    const ctx = useContext(ThemeCtx);
    if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
    return ctx;
}
